/**
 * Data Cache - CDN-like caching for instant navigation
 *
 * Caches API responses in memory + localStorage so screens show data
 * instantly instead of a loading spinner on revisit.
 *
 * - GET/read data is cached for 5 minutes
 * - Cache is invalidated automatically on POST/PUT/PATCH/DELETE
 * - Form/input data is NOT cached (only display data)
 */

import { Platform } from 'react-native';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_PREFIX = 'sms_cache_';
const MAX_STORAGE_ENTRIES = 80;

interface CacheEntry<T = any> {
  data: T;
  ts: number;
}

// In-memory cache (fast, survives navigation)
const memCache = new Map<string, CacheEntry>();

// Restore from localStorage on startup (survives page refresh)
if (Platform.OS === 'web') {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    const now = Date.now();
    for (const key of keys) {
      try {
        const entry: CacheEntry = JSON.parse(localStorage.getItem(key)!);
        if (now - entry.ts < CACHE_TTL) {
          memCache.set(key.slice(CACHE_PREFIX.length), entry);
        } else {
          localStorage.removeItem(key);
        }
      } catch {
        localStorage.removeItem(key);
      }
    }
  } catch {}
}

/** Get cached data. Returns null if expired or not found. */
export function getCachedData<T>(key: string): T | null {
  const entry = memCache.get(key);
  if (entry && Date.now() - entry.ts < CACHE_TTL) {
    return entry.data as T;
  }
  if (entry) memCache.delete(key);
  return null;
}

/** Store data in cache (memory + localStorage). */
export function setCachedData(key: string, data: any): void {
  const entry: CacheEntry = { data, ts: Date.now() };
  memCache.set(key, entry);

  if (Platform.OS === 'web') {
    try {
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));

      // Evict oldest entries if over limit
      const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
      if (keys.length > MAX_STORAGE_ENTRIES) {
        const entries = keys.map(k => {
          try {
            const e: CacheEntry = JSON.parse(localStorage.getItem(k)!);
            return { key: k, ts: e.ts };
          } catch {
            return { key: k, ts: 0 };
          }
        });
        entries.sort((a, b) => a.ts - b.ts);
        const toRemove = entries.slice(0, entries.length - MAX_STORAGE_ENTRIES);
        for (const { key: k } of toRemove) {
          localStorage.removeItem(k);
          memCache.delete(k.slice(CACHE_PREFIX.length));
        }
      }
    } catch {}
  }
}

/**
 * Invalidate cache entries matching a resource prefix.
 * Call this after mutations to ensure fresh data on next load.
 *
 * Examples:
 *   invalidateCache('goals')     - clears all goals cache
 *   invalidateCache('services')  - clears all services cache
 *   invalidateCache()            - clears everything
 */
export function invalidateCache(resourcePrefix?: string): void {
  if (!resourcePrefix) {
    memCache.clear();
    if (Platform.OS === 'web') {
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith(CACHE_PREFIX))
          .forEach(k => localStorage.removeItem(k));
      } catch {}
    }
    return;
  }

  const keysToDelete: string[] = [];
  memCache.forEach((_, key) => {
    if (key.startsWith(resourcePrefix)) {
      keysToDelete.push(key);
    }
  });
  for (const key of keysToDelete) {
    memCache.delete(key);
    if (Platform.OS === 'web') {
      try { localStorage.removeItem(CACHE_PREFIX + key); } catch {}
    }
  }
}
